import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, Eye, EyeOff, ArrowLeft, Sun, Moon } from 'lucide-react';
import { Mascot } from '../components/Mascot';
import { getRegistrationQuizSummary } from '../lib/quizResults';

// Skipya Logo with Robot Mascot
const SkipyaLogo = ({ size = 70 }) => (
  <Mascot size={size} />
);

export const RegisterPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { register } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const quizSummary = useMemo(
    () => getRegistrationQuizSummary(location.search, sessionStorage),
    [location.search]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(email, password, firstName, lastName);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-8 transition-colors duration-300 ${
      isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'
    }`}>
      {/* Background gradient */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 right-1/4 w-96 h-96 bg-[#81d8d0] rounded-full filter blur-[150px] ${isDark ? 'opacity-15' : 'opacity-20'}`} />
        <div className={`absolute bottom-1/4 left-1/4 w-80 h-80 bg-[#FF6B6B] rounded-full filter blur-[120px] ${isDark ? 'opacity-10' : 'opacity-15'}`} />
      </div>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className={`fixed top-6 right-6 p-3 rounded-xl transition-all duration-300 ${
          isDark 
            ? 'bg-white/5 hover:bg-white/10 text-yellow-400' 
            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
        }`}
        data-testid="theme-toggle"
      >
        {isDark ? <Sun size={20} strokeWidth={1.5} /> : <Moon size={20} strokeWidth={1.5} />}
      </button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Back to home */}
        <Link 
          to="/" 
          className={`flex items-center gap-2 mb-6 transition-colors ${
            isDark ? 'text-[#94A3B8] hover:text-[#81d8d0]' : 'text-gray-500 hover:text-[#81d8d0]'
          }`}
          data-testid="back-to-home"
        >
          <ArrowLeft size={18} />
          <span>Retour à l'accueil</span>
        </Link>

        <Card className={`backdrop-blur-xl border transition-colors duration-300 ${
          isDark 
            ? 'bg-[#1A1A2E]/80 border-white/10' 
            : 'bg-white/80 border-gray-200 shadow-xl'
        }`}>
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <SkipyaLogo size={70} />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className={`text-2xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Skipya
              </span>
            </div>
            <CardTitle className={`text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Rejoins Skipya !
            </CardTitle>
            <CardDescription className={isDark ? 'text-[#94A3B8]' : 'text-gray-500'}>
              Crée ton compte en quelques secondes
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {quizSummary && (
              <div className={`mb-5 rounded-2xl border p-4 ${
                isDark ? 'bg-[#81d8d0]/10 border-[#81d8d0]/20 text-[#CBD5E1]' : 'bg-[#81d8d0]/10 border-[#81d8d0]/25 text-gray-700'
              }`}>
                <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {quizSummary.title}
                </p>
                <p className="text-xs leading-5">
                  {quizSummary.detail}
                </p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`text-sm mb-1 block ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>Prénom</label>
                  <Input
                    type="text"
                    placeholder="Jean"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    data-testid="register-firstname"
                    className={`transition-colors ${
                      isDark 
                        ? 'bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#81d8d0]' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#81d8d0]'
                    }`}
                  />
                </div>
                <div>
                  <label className={`text-sm mb-1 block ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>Nom</label>
                  <Input
                    type="text"
                    placeholder="Dupont"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    data-testid="register-lastname"
                    className={`transition-colors ${
                      isDark 
                        ? 'bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#81d8d0]' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#81d8d0]'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className={`text-sm mb-1 block ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>Email</label>
                <Input
                  type="email"
                  placeholder="ton@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="register-email"
                  className={`transition-colors ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#81d8d0]' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#81d8d0]'
                  }`}
                />
              </div>
              
              <div>
                <label className={`text-sm mb-1 block ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>Mot de passe</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="register-password"
                    className={`pr-10 transition-colors ${
                      isDark 
                        ? 'bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#81d8d0]' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#81d8d0]'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                      isDark ? 'text-[#94A3B8] hover:text-[#81d8d0]' : 'text-gray-400 hover:text-[#81d8d0]'
                    }`}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className={`text-sm mb-1 block ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>Confirmer le mot de passe</label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="register-confirm-password"
                  className={`transition-colors ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus:border-[#81d8d0]' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#81d8d0]'
                  }`}
                />
              </div>

              {error && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-[#FF6B6B] text-sm text-center"
                >
                  {error}
                </motion.p>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="register-submit"
                className="w-full bg-[#81d8d0] hover:bg-[#6bc4bc] text-[#0F0F1A] font-semibold py-5 rounded-xl transition-all duration-300 hover:translate-y-[-1px] hover:shadow-lg"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Créer mon compte"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                Déjà un compte ?{' '}
                <Link 
                  to="/login" 
                  className="text-[#81d8d0] hover:text-[#6bc4bc] font-medium"
                  data-testid="go-to-login"
                >
                  Se connecter
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
